/**
 * restrict length of string in textareas
 * OR
 * Show a count of chars
 * OR show a dial representing % of available chars typed
 * If you just pass an $('el') it will count chars up
 * If you just pass an $('el'), with {maxCount: x} it will count chars down, and limit textarea
 */
export default class {
	static isDeletionEvent(inputType) {
		return [
			"deleteContentBackward",
			"deleteContentForward",
			"deleteWordForward",
			"deleteWordBackward",
			"deleteByCut",
			"historyUndo",
		].includes(inputType);
	}

	/** attach data to DOM element, call check once and add a listener
	 * @param {Object} $elem jQuery DOM element (should be textarea, input or contenteditable)
	 * @param {Object} opts {
		 	maxCount: Int - either the hard or soft char limit.
			limitType: *hard / soft - if the text is trimmed to the maxCount or not, and if the number counts down from max
			dialTargetSelector: String null - jquery selector for the knob.js text input
			countedCharsTargetSelector: String '.j-char-count' - the x / 100 or just x number that reflects the char count in the observeEl
																	If defined it will search the entire document for this element - so best to use a unique id or class.
																	If not defined it will search the parent of the textinput
		}
	 * Example dial with soft limit:
		 <%= t('global.characters_count') %> <span class='j-char-count'></span>/<%= task.suggested_char_count if task.suggested_char_count && task.suggested_char_count != 0 %>
		 <input type="text" class='j-char-count-dial'/>
		 Then in body ready:
		 new CharsUtil($('#actor-task-<%= actor_task.id %>'), {
				limitType: 'soft',
				maxCount: <%= task.suggested_char_count %>,
				dialTargetSelector: '.j-char-count-dial'
			});
	 */
	constructor($elem, opts) {
		opts = opts || {};

		this.maxCount = opts.maxCount || 0;
		this.limitType = opts.limitType || (this.maxCount == -1 ? "soft" : "hard");

		// Get our $(els);
		this.$observeEl = $elem;
		this.getPlainContentMethod = opts.getPlainContentMethod || this._getContent;
		this.$containerEl = opts.$containerEl || $elem.parent().parent();
		this.$popoverEl = opts.$popoverEl || $elem;
		this.inputEventSetup = opts.inputEventSetup || this._inputEventSetup;

		// Look outside container if special class provided
		if (opts.countedCharsTargetSelector !== undefined) {
			this.$countedCharsTargetEl = $(opts.countedCharsTargetSelector);
		} else {
			this.$countedCharsTargetEl = this.$containerEl.find("span.j-char-count");
		}

		if (!this.$countedCharsTargetEl.length) {
			// pp.log("CharsUtil ERROR, no counted chars target for:", $elem);
			return;
		}

		if (opts.dialTargetSelector !== undefined) {
			this.$dialTargetEl = this.$containerEl.find(opts.dialTargetSelector);
			this._setupDial();
		}
		// this._initLog();

		// Listen for changes
		this.inputEventSetup(this); // Pass instance into the setup method
	}

	// NB `charsUtilInstance` unused here
	_inputEventSetup(charsUtilInstance) {
		this.$observeEl.on("beforeinput", (event) => {
			this._handleBeforeInputEvent(event);
		});
		// Still make sure that the text isn't too lang after the input has taken place
		this.$observeEl.on("input", (event) => {
			this._handleInputEvent(event);
		});
		this.$observeEl.on("input paste change", () => {
			this._updateTextCount();
		});
		this.$observeEl.trigger("input");
	}

	_handleBeforeInputEvent(event) {
		// pp.log("into _handleBeforeInputEvent", event);
		let countedChars = this.getPlainContentMethod().length;

		let diff;
		const relevantEvent = event.originalEvent || event;
		// Case: something is being deleted
		// @ts-expect-error
		if (CharsUtil.isDeletionEvent(relevantEvent.inputType)) {
			// Backspace or DEL
			diff = 0;
		}
		// Case: Something that was previously removed via CTRL+Z (i.e. `historyUndo`) is being inserted again (via CTRL+Y)
		else if (relevantEvent.inputType == "historyRedo") {
			// pp.log("TODO: How to get the content of history redo?");
			diff = 0;
		}
		// Case: The browser is Chrome and a word is in the process of being composed,
		// e.g. the `relevantEvent.data` might be "foobar", even though the only newly inserted character was "r".
		// -> Only take the last character of `relevantEvent.data`
		// This only happens on Chrome in combination with the `inputType` 'insertCompositionText';
		// If a whole word is being inserted at once (from predictive text) then `relevantEvent.isComposing` is false, but *only* in Chrome.
		else if (
			bowser.chrome &&
			relevantEvent.isComposing &&
			relevantEvent.inputType == "insertCompositionText"
		) {
			diff = this._getDifferenceToMaxCount(
				countedChars + relevantEvent.data.substr(-1).length,
			);
		}
		// Case: The browser is Firefox and is in the process of being composed,
		// e.g. the `relevantEvent.data` might be "foobar", even though the only newly inserted character was "r".
		// -> Split the current input value by ' ', cut off the last (halfway done) word, then count the remaining characters.
		//
		else if (
			bowser.firefox &&
			relevantEvent.inputType == "insertCompositionText"
		) {
			countedChars = this.getPlainContentMethod()
				.split(" ")
				.slice(0, -1)
				.join(" ").length;
			// pp.notifications.display('notice', `slice: ${this.getPlainContentMethod().split(' ').slice(0, -1).join(' ')}`);
			diff = this._getDifferenceToMaxCount(
				countedChars + relevantEvent.data.length,
			);
		} else if (relevantEvent.inputType == "insertLineBreak") {
			diff = this._getDifferenceToMaxCount(countedChars + 1);
		} else if (relevantEvent.data !== undefined) {
			diff = this._getDifferenceToMaxCount(
				countedChars + relevantEvent.data.length,
			);
		}

		if (diff === null) {
			console.error("Could not determine diff");
		}

		// pp.log("CharsUtil check -- next text, counted chars, maxCount, diff", countedChars, this.maxCount, diff);

		if (diff < 0 && this.limitType === "hard") {
			// This will not always cancel the event, e.g. sometimes on Android when composing via predictive text the word or character will still be inserted even
			// though `preventDefault()` was called.
			event.preventDefault();
			if (
				event.originalEvent &&
				event.originalEvent.inputType == "insertFromPaste"
			) {
				this._handleTrimmedPaste();
			} else {
				// Hide the popovers from the paste event if present
				// Edge case: There might be a pad update that triggers 'change' right after the deferrent event popover is shown
				// and thus hides the popover immediately
				this.$popoverEl.popover("hide");
			}
		}
	}

	_handleInputEvent(event) {
		const currentContent = this.getPlainContentMethod();
		if (this._getDifferenceToMaxCount(currentContent.length) >= 0) {
			return;
		}
		this.$observeEl.val(currentContent.substr(0, this.maxCount));
	}

	_getContent() {
		if (["INPUT", "TEXTAREA"].indexOf(this.$observeEl[0].tagName) != -1) {
			return this.$observeEl.val();
		} else {
			return this.$observeEl.text();
		}
	}

	_setupDial() {
		this.$dialTargetEl.knob({
			min: 0,
			max: this.maxCount,
			fgColor: "#179EE3",
			displayInput: false,
			width: 20,
			height: 20,
			thickness: 0.3,
			linecap: "round",
			readOnly: true,
			rotation:
				I18n.t("meta.direction") == "rtl" ? "clockwise" : "anticlockwise",
		});
	}

	_setValAndUpdateDial(countedChars) {
		const dialVal = countedChars > this.maxCount ? this.maxCount : countedChars;
		this.$dialTargetEl.val(dialVal);
		this.$dialTargetEl.trigger("change");
	}

	_getDifferenceToMaxCount(countedChars) {
		return this.maxCount === 0 ? countedChars : this.maxCount - countedChars;
	}

	_handleTrimmedPaste() {
		if (this.$popoverEl.trigger !== undefined) {
			this.$popoverEl.trigger("autosize.resize"); // trigger manual autosize first, then show the popover
		}
		this.$popoverEl
			.popover({
				placement: "bottom",
				trigger: "manual",
				content: I18n.t("global.pasted_more_text_than_allowed"),
				template:
					'<div class="popover warn" role="tooltip"><div class="arrow"></div><div class="popover-content"></div></div>',
			})
			.popover("show");
		const that = this;
		setTimeout(function () {
			that.$popoverEl.popover("destroy");
		}, 5000);
	}

	_updateTextCount(event) {
		// pp.log("into _updateTextCount", this);
		const countedChars = this.getPlainContentMethod().length;
		if (this.limitType === "soft") {
			this.$countedCharsTargetEl.html(countedChars);
		} else {
			// Hard limit counts down
			const diff = this._getDifferenceToMaxCount(countedChars);
			if (diff >= 0) {
				this.$countedCharsTargetEl.html(diff);
			} else {
				this.$countedCharsTargetEl.html("0");
			}
		}

		// Update dial if there is one
		if (this.$dialTargetEl) {
			const dialAtMax = parseInt(this.$dialTargetEl.val()) === this.maxCount;
			const updateDial = !dialAtMax || countedChars < this.maxCount;
			if (updateDial) {
				this._setValAndUpdateDial(countedChars);
			}
		}
	}

	_initLog() {
		pp.log("*************MAXCHARS*************");
		pp.log("maxCount:", this.maxCount);
		pp.log("limitType:", this.limitType);
		pp.log("observeEl:", this.$observeEl);
		pp.log("containerEl:", this.$containerEl);
		pp.log("counted chars el:", this.$countedCharsTargetEl);

		if (this.$dialTargetEl) {
			pp.log("dialTargetEl:", this.$dialTargetEl);
		}
	}
}
